/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-router components
import { useLocation, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";

// Soft UI Dashboard PRO React example components
import Breadcrumbs from "components/Skill90Breadcrumb";
import NotificationItem from "examples/Items/NotificationItem";
import CheckSignOut from "Modals/CheckSignOut";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
} from "components/Skill90DashboardNavbar/styles";

// Soft UI Dashboard PRO React context
import { useSoftUIController, setTransparentNavbar, setMiniSidenav, setPlatform } from "context";

import { getUserData, setUserData } from "Utils/helpers";

// Images
import team2 from "assets/images/team-2.jpg";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, platform } = controller;
  const [notifications, setNotifications] = useState(null);
  const [openNotificationsMenu, setOpenNotificationsMenu] = useState(false);
  const [openUserMenu, setOpenUserMenu] = useState(false);
  const [showCreateModal, setCreateModal] = useState(false);
  const [signoutConfirmationTitle, setSignoutConfirmationTitle] = useState("");
  const [signoutConfirmationMessage, setSignoutConfirmationMessage] = useState("");
  const location = useLocation();
  const route = location.pathname.split("#")[0].split("/").slice(1);
  const userData = getUserData();
  const hidePlatformNamePaths =
    userData && userData.roles.find((item) => item.id == 1)
      ? [
          "/account",
          "/management/platforms",
          "/management/users",
          "/management/users/users-logs",
          "/management/users/edit/:id",
          "/management/export/competitions",
          "/management/export/participants",
          "/feed-settings/templates/:id/edit-filter/:id",
          "/feed-settings/filters/create-filter",
          "/feed-api/documentation",
          "/feed-api/feed-integration",
          "/feed-api/changelog",
          "/settlement/currentv:id",
          "/settlement/current",
          "/settlement/odd",
          "/settlement/finished",
          "/settlement/outrights",
          "/settlement/stucked-fixtures",
          "/settlement/advanced",
          "/settlement/fixture",
          "/settlement/confirmations",
          "/monitoring/fixture-live-view/",
        ]
      : [
          "/account",
          "/management/platforms",
          "/management/users",
          "/management/users/users-logs",
          "/management/users/edit/:id",
          "/management/export/competitions",
          "/management/export/participants",
          "/feed-settings/templates/:id/edit-filter/:id",
          "/feed-settings/filters/create-filter",
          "/feed-api/documentation",
          "/feed-api/feed-integration",
          "/feed-api/changelog",
          "/settlement/currentv:id",
          "/settlement/current",
          "/settlement/odd",
          "/settlement/finished",
          "/settlement/outrights",
          "/settlement/stucked-fixtures",
          "/settlement/advanced",
          "/settlement/fixture",
          "/settlement/confirmations",
          "/monitoring/fixture-live-view/",
        ];

  let navigate = useNavigate();
  const routeChange = (path) => {
    navigate(path);
    return;
  };

  if (!userData) {
    routeChange(`/login`);
  }

  const platforms = userData
    ? userData.platforms
      ? userData.platforms.map((platform) => ({
          value: platform.id,
          label: platform.name,
          logo: platform.logo,
          sorting: platform.sorting,
          odds_styles: platform.odds_styles,
        }))
      : []
    : [];

  let platformSelected =
    userData && userData.defaultPlatform
      ? userData.defaultPlatform
      : platforms && platforms.length > 0
      ? platforms[0]
      : { value: 0, label: "", logo: null, sorting: 0, odds_styles: [] };

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleOpenNotificationsMenu = (event) => setOpenNotificationsMenu(event.currentTarget);
  const handleCloseNotificationsMenu = () => setOpenNotificationsMenu(false);
  const handleOpenUserMenu = (event) => setOpenUserMenu(event.currentTarget);
  const handleCloseUserMenu = () => setOpenUserMenu(false);

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openNotificationsMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openNotificationsMenu)}
      onClose={handleCloseNotificationsMenu}
      sx={{ mt: 2 }}
    >
      {/* <NotificationItem
        image={<img src={team2} alt="person" />}
        title={["New message", "from Laur"]}
        date="13 minutes ago"
        onClick={handleCloseNotificationsMenu}
      />
      <NotificationItem
        image={<img src={logoSpotify} alt="person" />}
        title={["New album", "by Travis Scott"]}
        date="1 day"
        onClick={handleCloseNotificationsMenu}
      />
      <NotificationItem
        color="secondary"
        image={
          <Icon fontSize="small" sx={{ color: ({ palette: { white } }) => white.main }}>
            payment
          </Icon>
        }
        title={["", "Payment successfully completed"]}
        date="2 days"
        onClick={handleCloseNotificationsMenu}
      /> */}
    </Menu>
  );

  // Render the notifications menu
  const renderUserMenu = () => (
    <Menu
      anchorEl={openUserMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openUserMenu)}
      onClose={handleCloseUserMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem
        image={<img src={team2} alt="person" />}
        title={["Sign Out", ""]}
        info={["Logged in as: ", userData ? userData.roles[0].name : ""]}
        onClick={(e) => handleSignOut(e)}
      />
      <NotificationItem
        title={["Edit Account", ""]}
        info={[]}
        onClick={(e) => routeChange("/account")}
      />
    </Menu>
  );

  const handleSignOut = (e) => {
    e.preventDefault();
    //navigate("/signout");
    setSignoutConfirmationTitle("Signout check");
    setSignoutConfirmationMessage("Are you sure you want to signout?");
    setCreateModal(true);
    setOpenUserMenu(false);
  };

  const handleCloseModal = (update) => {
    if (update) {
      //console.log("Creation completed, please Update!");
    } else {
      //console.log("Creation canceled, do nothing!");
    }

    setCreateModal(false);
  };

  const handleSubUserChange = ({ label, value, logo, sorting, odds_styles }) => {
    const tmpPlatform = {
      label: label,
      value: value,
      logo: logo,
      sorting: sorting,
      odds_styles: odds_styles,
    };
    setUserData({ key: "defaultPlatform", value: tmpPlatform });
    platformSelected = tmpPlatform;
    setPlatform(dispatch, tmpPlatform);
  };

  const showPlatform = () => {
    let path = location.pathname.replace(/[0-9]+/g, ":id");
    if (path && hidePlatformNamePaths.indexOf(path) > -1) return true;
    return false;
  };

  useEffect(() => {
    setPlatform(dispatch, platformSelected);
  }, [platformSelected.label]);

  useEffect(() => {
    platformSelected = { label: "", value: 0, logo: null, sorting: 0, odds_styles: [] };
    if (platform)
      platformSelected = {
        label: platform.label,
        value: platform.value,
        logo: platform.logo,
        sorting: platform.sorting,
        odds_styles: platform.odds_styles,
      };
    setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);

    if (userData && platform && !userData.defaultPlatform) {
      setUserData({
        key: "defaultPlatform",
        value: {
          label: platform.label,
          value: platform.value,
          logo: platform.logo,
          sorting: platform.sorting,
          odds_styles: platform.odds_styles,
        },
      });
    }
  }, [platform]);

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
    >
      <CheckSignOut
        reference={"/login"}
        showModal={showCreateModal}
        title={signoutConfirmationTitle}
        message={signoutConfirmationMessage}
        signalCloseModal={handleCloseModal}
      ></CheckSignOut>
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <SoftBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <SoftBox color={light ? "white" : "inherit"}>
            {/* <Icon fontSize="medium" sx={navbarDesktopMenu} onClick={handleMiniSidenav}>
              {miniSidenav ? "menu_open" : "menu"}
            </Icon> */}
            <IconButton size="small" color="inherit" onClick={handleMiniSidenav}>
              <Icon className={light ? "text-white" : "text-dark"}>
                {miniSidenav ? "menu_open" : "menu"}
              </Icon>
            </IconButton>
          </SoftBox>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </SoftBox>
        {isMini ? null : (
          <SoftBox sx={(theme) => navbarRow(theme, { isMini })}>
            <SoftBox color={light ? "white" : "inherit"} className="navbar-menu-holder">
              <SoftBox>
                <SoftSelect
                  value={platformSelected}
                  defaultValue={platformSelected}
                  isDisabled={showPlatform()}
                  options={platforms.map((entry) => ({
                    value: entry.value,
                    label: entry.label,
                    logo: entry.logo,
                    sorting: entry.sorting,
                    odds_styles: entry.odds_styles,
                  }))}
                  onChange={handleSubUserChange}
                  size="small"
                  className={showPlatform() ? "disabled-form-item" : ""}
                />
              </SoftBox>

              <IconButton
                size="small"
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={notifications ? handleOpenNotificationsMenu() : null}
                isdisabled={notifications ? "false" : "true"}
              >
                <Icon
                  title={notifications ? "" : "not any available notification"}
                  className={notifications ? "text-dark" : "text-white"}
                  sx={{ opacity: notifications ? 1 : 0.2 }}
                >
                  notifications
                </Icon>
              </IconButton>
              {renderMenu()}
              {/* <Link to="/signout"> */}
              <SoftBox>
                <IconButton
                  className="user-menu-button"
                  sx={navbarIconButton}
                  size="medium"
                  onClick={handleOpenUserMenu}
                >
                  <SoftTypography
                    className="singout-label"
                    variant="button"
                    marginRight="4px"
                    color={light ? "white" : "secondary"}
                  >
                    {userData ? userData.username : " "}
                  </SoftTypography>
                  <Icon
                    className="singout-icon"
                    sx={({ palette: { dark, white, primary } }) => ({
                      color: light ? white.main : primary.main,
                    })}
                  >
                    account_circle
                  </Icon>
                </IconButton>
              </SoftBox>

              {renderUserMenu()}
              {/* </Link> */}
            </SoftBox>
          </SoftBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
